if (!customElements.get('mamami-signature-stage')) {
  customElements.define(
    'mamami-signature-stage',
    class MamamiSignatureStage extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.items = Array.from(this.querySelectorAll('[data-stage-product]'));
        if (!this.items.length) return;

        this.initialized = true;
        this.currentIndex = 0;
        this.isAnimating = false;
        this.pointerId = null;
        this.dragStartX = 0;
        this.dragLastX = 0;
        this.dragStartTime = 0;
        this.suppressClick = false;
        this.navigationRunId = 0;
        this.activeNavigationRun = null;
        this.backwardState = null;
        this.animationFrameIds = new Set();
        this.timeoutIds = new Set();
        this.originalTabIndexes = new WeakMap();
        this.liveRegion = this.querySelector('[data-stage-live]');
        this.backgroundContainer = this.closest('[data-signature-background]');
        this.previousButton = this.querySelector('[data-stage-previous]');
        this.nextButton = this.querySelector('[data-stage-next]');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerMove = this.handlePointerMove.bind(this);
        this.onPointerUp = this.handlePointerUp.bind(this);
        this.onKeyDown = this.handleKeyDown.bind(this);
        this.onClick = this.handleClick.bind(this);
        this.onPreviousClick = this.handlePreviousClick.bind(this);
        this.onNextClick = this.handleNextClick.bind(this);
        this.onTransitionEnd = this.handleTransitionEnd.bind(this);
        this.onResize = this.handleResize.bind(this);
        this.onThemeEditorEvent = this.handleThemeEditorEvent.bind(this);

        this.addEventListener('pointerdown', this.onPointerDown);
        this.addEventListener('pointermove', this.onPointerMove);
        this.addEventListener('pointerup', this.onPointerUp);
        this.addEventListener('pointercancel', this.onPointerUp);
        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('click', this.onClick, true);
        this.addEventListener('transitionend', this.onTransitionEnd);
        this.previousButton?.addEventListener('click', this.onPreviousClick);
        this.nextButton?.addEventListener('click', this.onNextClick);
        window.addEventListener('resize', this.onResize, { passive: true });
        document.addEventListener('shopify:section:select', this.onThemeEditorEvent);
        document.addEventListener('shopify:block:select', this.onThemeEditorEvent);

        this.classList.add('is-enhanced');
        this.updateBackground(this.currentIndex);
        this.render(false);
      }

      disconnectedCallback() {
        if (!this.initialized) return;

        this.removeEventListener('pointerdown', this.onPointerDown);
        this.removeEventListener('pointermove', this.onPointerMove);
        this.removeEventListener('pointerup', this.onPointerUp);
        this.removeEventListener('pointercancel', this.onPointerUp);
        this.removeEventListener('keydown', this.onKeyDown);
        this.removeEventListener('click', this.onClick, true);
        this.removeEventListener('transitionend', this.onTransitionEnd);
        this.previousButton?.removeEventListener('click', this.onPreviousClick);
        this.nextButton?.removeEventListener('click', this.onNextClick);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('shopify:section:select', this.onThemeEditorEvent);
        document.removeEventListener('shopify:block:select', this.onThemeEditorEvent);

        this.animationFrameIds.forEach((id) => cancelAnimationFrame(id));
        this.timeoutIds.forEach((id) => clearTimeout(id));
        this.animationFrameIds.clear();
        this.timeoutIds.clear();

        if (this.pointerId !== null && this.hasPointerCapture?.(this.pointerId)) {
          this.releasePointerCapture(this.pointerId);
        }

        this.initialized = false;
      }

      render(announce = true) {
        const count = this.items.length;
        const nextIndex = (this.currentIndex + 1) % count;

        this.items.forEach((item, index) => {
          const isActive = index === this.currentIndex;
          const isNext = count > 1 && index === nextIndex;
          const preview = item.querySelector('[data-stage-preview]');
          const details = item.querySelector('[data-stage-details]');

          item.className = 'mamami-signature__product';
          item.classList.toggle('is-active', isActive);
          item.classList.toggle('is-next', isNext);
          item.setAttribute('aria-hidden', isActive || isNext ? 'false' : 'true');
          item.toggleAttribute('inert', !isActive && !isNext);

          if (isActive) {
            item.setAttribute('aria-label', `Product ${index + 1} of ${count}`);
          } else {
            item.removeAttribute('aria-label');
          }

          preview.disabled = !isNext;
          preview.tabIndex = isNext ? 0 : -1;
          details.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          this.setDetailsFocusable(details, isActive);
        });

        if (announce && this.liveRegion) {
          const item = this.items[this.currentIndex];
          this.liveRegion.textContent = `Product ${this.currentIndex + 1} of ${count}: ${item.dataset.productTitle}`;
        }
      }

      setDetailsFocusable(details, focusable) {
        details.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          if (!this.originalTabIndexes.has(element)) {
            this.originalTabIndexes.set(element, element.getAttribute('tabindex'));
          }

          if (focusable) {
            const original = this.originalTabIndexes.get(element);
            if (original === null) element.removeAttribute('tabindex');
            else element.setAttribute('tabindex', original);
          } else {
            element.setAttribute('tabindex', '-1');
          }
        });
      }

      handlePointerDown(event) {
        if (this.isAnimating || event.button !== undefined && event.button !== 0) return;
        if (event.target.closest('[data-stage-previous], [data-stage-next], [data-stage-details]')) return;

        this.pointerId = event.pointerId;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.dragLastX = event.clientX;
        this.dragStartTime = performance.now();
        this.suppressClick = false;
        this.setPointerCapture(event.pointerId);
      }

      handlePointerMove(event) {
        if (event.pointerId !== this.pointerId || this.isAnimating) return;

        this.dragLastX = event.clientX;
        const delta = event.clientX - this.dragStartX;
        const verticalDelta = event.clientY - this.dragStartY;
        if (Math.abs(delta) > 8) this.suppressClick = true;
        if (Math.abs(delta) > Math.abs(verticalDelta) && event.cancelable) event.preventDefault();

        const activeMedia = this.items[this.currentIndex].querySelector('[data-stage-preview]');
        const nextMedia = this.items[(this.currentIndex + 1) % this.items.length].querySelector('[data-stage-preview]');
        const constrained = Math.max(-120, Math.min(120, delta));
        const progress = Math.min(1, Math.abs(constrained) / 120);

        if (delta < 0) {
          this.cancelBackwardPreparation();
          activeMedia.style.transform = `translate3d(calc(-50% + ${constrained}px), -50%, 0) scale(${1 - progress * 0.08})`;
          activeMedia.style.opacity = `${1 - progress * 0.45}`;
          nextMedia.style.opacity = `${0.5 + progress * 0.5}`;
        } else if (delta > 0) {
          this.prepareBackwardNavigation();
          this.applyBackwardProgress(progress);
        }
      }

      handlePointerUp(event) {
        if (event.pointerId !== this.pointerId) return;

        if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
        this.pointerId = null;

        if (event.type === 'pointercancel') {
          this.cancelPointerGesture();
          return;
        }

        const delta = this.dragLastX - this.dragStartX;
        const elapsed = Math.max(1, performance.now() - this.dragStartTime);
        const velocity = delta / elapsed;
        if (Math.abs(delta) >= 48 || Math.abs(velocity) >= 0.45) {
          if (delta > 0) {
            this.navigate(-1, 'swipe');
          } else {
            this.cancelBackwardPreparation();
            this.clearPointerStyles();
            this.navigate(1, 'swipe');
          }
        } else {
          this.cancelPointerGesture();
        }
      }

      clearPointerStyles() {
        this.items.forEach((item) => {
          const media = item.querySelector('[data-stage-preview]');
          media.style.removeProperty('transform');
          media.style.removeProperty('opacity');
          item.querySelector('[data-stage-details]').style.removeProperty('opacity');
        });
      }

      prepareBackwardNavigation(requestedIndex = null) {
        if (this.backwardState && (requestedIndex === null || this.backwardState.previousIndex === requestedIndex)) {
          return this.backwardState;
        }
        if (this.backwardState) this.cancelBackwardPreparation();

        const activeItem = this.items[this.currentIndex];
        const previousIndex = requestedIndex ?? (this.currentIndex - 1 + this.items.length) % this.items.length;
        const previousItem = this.items[previousIndex];
        const nextItem = this.items[(this.currentIndex + 1) % this.items.length];
        const activeRect = activeItem.querySelector('[data-stage-preview]').getBoundingClientRect();
        const nextRect = nextItem.querySelector('[data-stage-preview]').getBoundingClientRect();

        this.backwardState = {
          activeItem,
          previousItem,
          nextItem,
          previousIndex,
          offsetX: Math.abs((nextRect.left + nextRect.width / 2) - (activeRect.left + activeRect.width / 2)),
          offsetY: Math.abs((nextRect.top + nextRect.height / 2) - (activeRect.top + activeRect.height / 2)),
          committed: false,
        };
        previousItem.classList.add('is-backward-entering');
        nextItem.classList.add('is-backward-old-next');
        return this.backwardState;
      }

      applyBackwardProgress(progress) {
        const state = this.backwardState;
        if (!state) return;

        const boundedProgress = Math.max(0, Math.min(1, progress));
        this.backwardDragProgress = boundedProgress;
        const remaining = 1 - boundedProgress;
        const activeMedia = state.activeItem.querySelector('[data-stage-preview]');
        const previousMedia = state.previousItem.querySelector('[data-stage-preview]');
        const nextMedia = state.nextItem.querySelector('[data-stage-preview]');
        activeMedia.style.transform = `translate3d(calc(-50% + ${state.offsetX * boundedProgress}px), calc(-50% + ${state.offsetY * boundedProgress}px), 0) scale(${1 - boundedProgress * 0.5})`;
        activeMedia.style.opacity = `${1 - boundedProgress * 0.5}`;
        previousMedia.style.transform = `translate3d(calc(-50% - ${state.offsetX * remaining}px), calc(-50% + ${state.offsetY * remaining}px), 0) scale(${0.5 + boundedProgress * 0.5})`;
        previousMedia.style.opacity = `${0.5 + boundedProgress * 0.5}`;
        nextMedia.style.opacity = `${0.5 * remaining}`;
        state.activeItem.querySelector('[data-stage-details]').style.opacity = `${remaining}`;
      }

      cancelBackwardPreparation() {
        const state = this.backwardState;
        if (!state || state.committed) return;

        state.previousItem.classList.remove('is-backward-entering');
        state.nextItem.classList.remove('is-backward-old-next');
        this.clearPointerStyles();
        this.backwardState = null;
        this.backwardDragProgress = null;
      }

      cancelPointerGesture() {
        this.cancelBackwardPreparation();
        this.clearPointerStyles();
      }

      handleClick(event) {
        if (this.suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
          return;
        }

        const preview = event.target.closest('[data-stage-preview]');
        if (preview && preview.closest('[data-stage-product]')?.classList.contains('is-next')) {
          event.preventDefault();
          this.navigate(1, 'preview');
        }
      }

      handlePreviousClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.navigate(-1, 'previous-button');
      }

      handleNextClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.navigate(1, 'next-button');
      }

      handleKeyDown(event) {
        if (event.defaultPrevented || this.isAnimating) return;

        let targetIndex;
        let direction = 'forward';

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigate(1, 'keyboard');
          return;
        }
        else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.navigate(-1, 'keyboard');
          return;
        } else if (event.key === 'Home') {
          targetIndex = 0;
          direction = targetIndex < this.currentIndex ? 'backward' : 'forward';
        } else if (event.key === 'End') {
          targetIndex = this.items.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        if (targetIndex !== this.currentIndex) this.commitNavigation(targetIndex, direction, 'keyboard');
      }

      navigate(direction, source) {
        if (direction !== 1 && direction !== -1) return;

        const targetIndex = (this.currentIndex + direction + this.items.length) % this.items.length;
        this.commitNavigation(targetIndex, direction === 1 ? 'forward' : 'backward', source);
      }

      commitNavigation(targetIndex, direction, source) {
        if (this.isAnimating || targetIndex === this.currentIndex) return;

        this.isAnimating = true;
        const runToken = ++this.navigationRunId;
        this.activeNavigationRun = runToken;
        this.navigationSource = source;
        this.updateBackground(targetIndex);
        const currentItem = this.items[this.currentIndex];
        const targetItem = this.items[targetIndex];
        const followingItem = this.items[(targetIndex + 1) % this.items.length];
        this.navigationTargetIndex = targetIndex;
        this.navigationTargetMedia = targetItem.querySelector('[data-stage-preview]');

        if (direction === 'forward') {
          this.cancelBackwardPreparation();
          this.clearPointerStyles();
          this.items.forEach((item) => {
            item.classList.remove('is-next', 'is-following', 'is-transitioning', 'is-transitioning-back', 'is-dragging-back');
            item.querySelector('[data-stage-preview]').disabled = true;
            item.querySelector('[data-stage-preview]').tabIndex = -1;
            if (item !== currentItem) {
              item.setAttribute('aria-hidden', 'true');
              item.setAttribute('inert', '');
            }
          });
          currentItem.classList.add('is-active');
          targetItem.classList.add('is-next');
          if (followingItem !== currentItem && followingItem !== targetItem) followingItem.classList.add('is-following');
        } else {
          const state = this.prepareBackwardNavigation(targetIndex);
          state.committed = true;
          this.items.forEach((item) => {
            item.querySelector('[data-stage-preview]').disabled = true;
            item.querySelector('[data-stage-preview]').tabIndex = -1;
            if (item !== currentItem) {
              item.setAttribute('aria-hidden', 'true');
              item.setAttribute('inert', '');
            }
          });
          state.nextItem.classList.remove('is-next');
          state.nextItem.classList.add('is-backward-old-next');
          state.previousItem.classList.add('is-backward-entering');
          this.applyBackwardProgress(this.backwardDragProgress || 0);
          this.navigationTargetMedia.getBoundingClientRect();
        }

        const frameId = requestAnimationFrame(() => {
          this.animationFrameIds.delete(frameId);
          if (this.activeNavigationRun !== runToken) return;
          this.classList.add(direction === 'forward' ? 'is-animating-forward' : 'is-animating-backward');
          if (direction === 'backward') this.clearPointerStyles();
        });
        this.animationFrameIds.add(frameId);

        const timeoutId = window.setTimeout(() => {
          this.timeoutIds.delete(timeoutId);
          if (this.navigationTimeoutId === timeoutId) this.navigationTimeoutId = null;
          this.finishNavigation(runToken);
        }, this.reducedMotion.matches ? 120 : 625);
        this.navigationTimeoutId = timeoutId;
        this.timeoutIds.add(timeoutId);
      }

      handleTransitionEnd(event) {
        if (!this.isAnimating || event.target !== this.navigationTargetMedia) return;
        if (event.propertyName !== 'transform') return;
        this.finishNavigation(this.activeNavigationRun);
      }

      finishNavigation(runToken) {
        if (!this.isAnimating || runToken !== this.activeNavigationRun) return;

        if (this.navigationTimeoutId) {
          clearTimeout(this.navigationTimeoutId);
          this.timeoutIds.delete(this.navigationTimeoutId);
          this.navigationTimeoutId = null;
        }

        this.classList.remove('is-animating-forward', 'is-animating-backward');
        this.currentIndex = this.navigationTargetIndex;
        this.items.forEach((item) => {
          item.classList.remove(
            'is-following',
            'is-transitioning',
            'is-transitioning-back',
            'is-dragging-back',
            'is-backward-entering',
            'is-backward-old-next'
          );
        });
        this.clearPointerStyles();
        this.backwardState = null;
        this.backwardDragProgress = null;
        this.pointerId = null;
        this.isAnimating = false;
        this.activeNavigationRun = null;
        this.navigationTargetIndex = null;
        this.navigationTargetMedia = null;
        this.navigationSource = null;
        this.render(true);
      }

      updateBackground(index) {
        if (!this.backgroundContainer) return;

        const color = this.items[index]?.dataset.stageBackground;
        const background = color || 'rgb(var(--color-background))';
        this.backgroundContainer.style.setProperty('--signature-active-background', background);
        this.backgroundContainer.style.backgroundColor = background;
      }

      handleResize() {
        if (this.resizeFrameId) {
          cancelAnimationFrame(this.resizeFrameId);
          this.animationFrameIds.delete(this.resizeFrameId);
        }
        this.resizeFrameId = requestAnimationFrame(() => {
          this.animationFrameIds.delete(this.resizeFrameId);
          this.resizeFrameId = null;
          if (!this.isAnimating) this.cancelPointerGesture();
        });
        this.animationFrameIds.add(this.resizeFrameId);
      }

      handleThemeEditorEvent(event) {
        if (event.target.contains(this) || this.contains(event.target)) this.render(false);
      }
    }
  );
}
